import React, { useEffect, useState } from "react";
import { Field, Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useParams, useNavigate } from "react-router-dom";
import { useMask } from "@react-input/mask";
import loginStyles from "pages/Login/Login.module.scss";
import { LoginLayout } from "pages/Login/Login";
import Button, { ButtonWrapper } from "components/Button/Button";
import {
  FormControl,
  FormFeedback,
  FormLabel,
  FormRow,
  FormStep,
} from "components/Form/Form";
import { validateCPF } from "utils/validateCPF";
import { api } from "services";
import styles from "./FindEmail.module.scss";

const cpfSchema = Yup.object().shape({
  cpf: Yup.string()
    .required("Campo obrigatório")
    .test("cpf", "CPF Inválido", (value) => validateCPF(value)),
});

export default function FindEmail() {
  const navigate = useNavigate();
  const { step } = useParams();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!step) {
      navigate("/consultar-email/informar-cpf");
    }
  }, [step, navigate]);

  const cpfRef = useMask({
    mask: "___.___.___-__",
    replacement: { _: /\d/ },
  });

  const handleSubmit = async (values, actions) => {
    const formatedCpf = values.cpf.replace(/\D/g, "");
    const loaderId = toast.loading("Consultando CPF...");
    setLoading(true);

    try {
      const { data } = await api.get(`/users/${formatedCpf}`);

      setEmail(data.existing_user?.email || data?.seduc?.institutional_email);
      navigate("/consultar-email/resultado");
    } catch (error) {
      if (error.response?.status === 404) {
        actions.setFieldError(
          "cpf",
          "CPF não encontrado no iSeduc. Entre em contato com sua escola para verificar seu cadastro.",
        );
      } else {
        actions.setFieldError(
          "cpf",
          error.response?.data?.error || "Erro ao consultar CPF.",
        );
      }
    } finally {
      toast.dismiss(loaderId);
      setLoading(false);
    }
  };

  const handleCopyEmail = async () => {
    await navigator.clipboard.writeText(email);
    toast.success("E-mail copiado!");
  };

  const handleBackButton = () => {
    if (step === "resultado") return "/consultar-email/informar-cpf";
    return "/login";
  };

  return (
    <LoginLayout back={handleBackButton()} contentWidth={550}>
      <FormStep show={step === "informar-cpf"}>
        <Formik
          initialValues={{ cpf: "" }}
          validationSchema={cpfSchema}
          onSubmit={handleSubmit}
        >
          {(props) => (
            <Form>
              <div className={loginStyles.body}>
                <div>
                  <h1 className={loginStyles.title}>
                    Vamos consultar seus dados na <strong>Rede SEDUC.</strong>
                  </h1>
                  <p className={loginStyles.description}>
                    Digite seu CPF para localizarmos seu e-mail de acesso
                  </p>
                </div>

                <FormRow>
                  <FormLabel htmlFor="cpf">Digite o seu CPF</FormLabel>
                  <FormControl
                    id="cpf"
                    name="cpf"
                    type="text"
                    value={props.values.cpf}
                    invalid={props.errors.cpf && props.touched.cpf}
                    placeholder="000.000.000-00"
                    ref={cpfRef}
                  />
                  <ErrorMessage name="cpf">
                    {(msg) => <FormFeedback invalid={true}>{msg}</FormFeedback>}
                  </ErrorMessage>
                </FormRow>
              </div>

              <ButtonWrapper>
                <Button
                  modifiers={["primary"]}
                  type="submit"
                  style={{ minWidth: 134 }}
                  disabled={props.isSubmitting || !props.values.cpf || loading}
                >
                  Consultar
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>

      <FormStep show={step === "resultado"}>
        {email ? (
          <>
            <div className={loginStyles.body}>
              <div className={loginStyles.textGroup}>
                <h1
                  className={loginStyles.title}
                  style={{ fontSize: "1.8rem" }}
                >
                  <strong>E-mail localizado com sucesso!</strong>
                  <br />
                  Este é o seu novo acesso oficial para acessar a plataforma:
                </h1>
              </div>

              <FormRow>
                <div className={styles["email-result"]}>
                  <span>{email}</span>
                  <Button
                    modifiers={["outline"]}
                    onClick={handleCopyEmail}
                    style={{
                      padding: "8px 16px",
                      fontSize: "1.4rem",
                    }}
                  >
                    Copiar e-mail
                  </Button>
                </div>
              </FormRow>

              <p className={loginStyles.body} style={{ fontSize: "1.8rem" }}>
                Copie o e-mail acima. Você vai precisar colocá-lo na próxima
                tela para entrar na plataforma.
              </p>
            </div>
            <ButtonWrapper>
              <Button
                modifiers={["outline"]}
                onClick={() =>
                  navigate("/login", { state: { skipAccessInfoModal: true } })
                }
                style={{ minWidth: 134 }}
              >
                Continuar
              </Button>
            </ButtonWrapper>
          </>
        ) : (
          <div className={loginStyles.body}>
            <div
              className={loginStyles.textGroup}
              style={{ fontSize: "1.8rem", gap: 0 }}
            >
              <p style={{ marginBottom: "2rem", fontWeight: "bold" }}>
                Desculpe, mas não conseguimos encontrar seu e-mail.
              </p>
              <h1 className={loginStyles.title}>
                Vá até a sua escola para corrigir seus dados. Depois disso, é só
                voltar aqui e finalizar seu cadastro para ter acesso aos
                conteúdos e começar a estudar!
              </h1>
            </div>
          </div>
        )}
      </FormStep>
    </LoginLayout>
  );
}
